<template>
  <div class="container open-positions-list">
    <OpenPositionsModal />
  </div>
</template>

<script lang="ts" setup>
import type { OpenPositionsListModel } from '~/models'
import { useOpenPositionModal } from '~/stores/modals/open-position-modal'
import type { OpenPositionSearchRequest } from '~/types/search-types/OpenPositionSearchRequest'
import type { SearchResultOpenPositionItem } from '~/types/SearchResultItem'

const tabButtonTexts = ref<string[]>([]);
const activeTabText = ref<string | null>('All');

onMounted(async () => {
  await getTaxonomies()
  await handleDisplayOfItems()
  const tabButtons = document.querySelectorAll('.nav-item[role="presentation"] button');
  tabButtonTexts.value = Array.from(tabButtons).map(button => button.textContent?.trim() || '');
  const updateActiveTab = () => {
    const activeTabButton = document.querySelector('.nav-item[role="presentation"] .nav-link.active');
    activeTabText.value = activeTabButton?.textContent?.trim() || null;
    openPositionsModal.selectedCategory = activeTabText.value || '';
  };


  const observer = new MutationObserver(updateActiveTab);
  document.querySelectorAll('.nav-item[role="presentation"] button').forEach(button => {
    observer.observe(button, { attributes: true, attributeFilter: ['class'] });
  });

})
const loadStore = useLoadingStore()
const openPositionsModal = useOpenPositionModal()
const usePaginationModal = usePaginationStore()

const props = defineProps<{ model: OpenPositionsListModel }>()

const propsData = {
  title: props.model?.elements.title.value,
  filters: props.model?.elements.filters.value,
  layout: firstOrDefault(props.model?.elements.layout.value),
  toc: firstOrDefault(props.model?.elements.tableOfContentLinksTableOfContents.value)
}
/// handling 'previous page (back)' btn
openPositionsModal.resetStates()
openPositionsModal.unMutableLayoutIsFullList =
  firstOrDefault(propsData.layout)?.codename.toLowerCase() == 'full_list'
openPositionsModal.layoutIsFullList = openPositionsModal.unMutableLayoutIsFullList

const fetchCareerPages = async () => {
  const body: OpenPositionSearchRequest = {
    selectedCategory: openPositionsModal.selectedCategory,
    selectedLocation: openPositionsModal.selectedLocation,
    selectedDepartment: openPositionsModal.selectedDepartment,
    selectedSortValue: openPositionsModal.selectedSortValue,
    searchValue: openPositionsModal.searchValue,
    page: usePaginationModal.currentPage,
    size: openPositionsModal.size
  }
  return await fetchOpenPositionSearch(body)
}

async function handleDisplayOfItems() {
  loadStore.setLoading(true)
  const { data } = await fetchCareerPages()
  openPositionsModal.setItems(data?.items as SearchResultOpenPositionItem[])
  openPositionsModal.setTotalResults(data?.totalCount || 0)
  usePaginationModal.setTotalPages(
    Math.ceil(openPositionsModal.totalResults / openPositionsModal.size)
  )

  loadStore.setLoading(false)
}

async function getTaxonomies() {
  const categories = props.model?.elements.filters.value
  openPositionsModal.setCareerCategories(categories.map((t) => t.name))

  const locations = await fetchLocations()
  const locationsNames = locations?.data?.items?.map((p: any) => {
    return {
      name: p.elements.title.value,
      codename: p.system.codename
    }
  })
  openPositionsModal.setLocations(locationsNames || [])

  const taxonomy = await fetchTaxonomy()
  const careerCategories =
    taxonomy?.data?.find((t) => t.system.codename === 'department')?.terms || []

  openPositionsModal.setDepartments(careerCategories.map((t) => t.name) || [])
}

async function readMoreItems() {
  if (
    !openPositionsModal.readMore ||
    openPositionsModal.items.length >= openPositionsModal.totalResults
  )
    return
  loadStore.setLoading(true)
  usePaginationModal.changePage(usePaginationModal.currentPage + 1)
  const { data } = await fetchCareerPages()
  openPositionsModal.addItems(data?.items as SearchResultOpenPositionItem[])
  openPositionsModal.setReadMore(false)
  loadStore.setLoading(false)
}

watch(
  () => [
    openPositionsModal.searchValue,
    openPositionsModal.selectedCategory,
    openPositionsModal.layoutIsFullList,
    openPositionsModal.selectedLocation,
    openPositionsModal.selectedDepartment,
    openPositionsModal.selectedSortValue,
    openPositionsModal.isGrid,
    openPositionsModal.size,
    openPositionsModal.selectedSortValue,
    usePaginationModal.currentPage
  ],
  async () => {
    await handleDisplayOfItems()
  }
)

watch(
  () => [openPositionsModal.readMore],
  async () => {
    await readMoreItems()
  }
)
</script>
